
import { CorporatePaymentCost, CorporateFinancialProfile } from "@/types/CardProgram";
import { TradeProfile } from "@/types/Campaign";
import { CorporateCardRebateProgram } from "@/types/CardProgram";

// ── Shared financial defaults ──
export const FINANCIAL_DEFAULTS = {
  WACC_RATE: 0.08,
  INFLATION_RATE: 0.03,
  DPO_DAYS: 30,
  DISCOUNT_RATE: 0.08,
} as const;

// ── Payment method default cost structures ──
export const PAYMENT_METHOD_DEFAULTS: Record<
  string,
  { percentage_cost: number; cost_per_transaction: number; processing_days: number }
> = {
  Plastiq: {
    percentage_cost: 0.029, // 2.9% processing fee
    cost_per_transaction: 0, // No fixed per-transaction fee
    processing_days: 3, // ACH/check settlement timeline
  },
};

// ── Spend bucket definitions ──
export const SPEND_BUCKETS = {
  SMALL: { min: 0, max: 10000, label: '<$10k', enrollmentRate: 33 },
  MEDIUM: { min: 10000, max: 100000, label: '$10k-$100k', enrollmentRate: 27 },
  LARGE: { min: 100000, max: 1000000, label: '$100k-$1MM', enrollmentRate: 18 },
  STRATEGIC_MID: { min: 1000000, max: 5000000, label: '$1MM-$5MM', enrollmentRate: 9 },
  STRATEGIC_HIGH: { min: 5000000, max: Infinity, label: '>$5MM', enrollmentRate: 2 }
} as const;

// ── Transaction type definitions ──
export const TRANSACTION_TYPES = {
  TOTAL_IN_NETWORK: { label: 'Total In-Network', enrollmentRate: 49 },
  LEVEL_III: { label: 'Level III', enrollmentRate: 42 },
  LEVEL_II: { label: 'Level II', enrollmentRate: 30 },
  SMALL_ANNUAL: { label: '<$10k per year', enrollmentRate: 29 },
  MATCHED: { label: 'Matched', enrollmentRate: 30 },
  UNMATCHED: { label: 'Unmatched', enrollmentRate: 19 }
} as const;

// ── Revenue share rates ──
export const REVENUE_SHARE_RATES = {
  'Card (Standard)': 1.00,
  'Card (Reduced IC)': 0.50,
  'AP+': 1.00
} as const;

// ── Error classes for missing required inputs ──

/**
 * Thrown when one or more required financial inputs are not configured.
 * Carries the corporate ID, a pre-built settings URL, the list of missing
 * fields, and a human-readable settings label so UI consumers can render
 * an actionable clickable link directing the user to configure them.
 */
export class MissingFinancialInputError extends Error {
  public readonly corporateId: number;
  public readonly settingsUrl: string;
  public readonly settingsLabel: string;
  public readonly fields: string[];

  constructor(fields: string | string[], corporateId: number) {
    const fieldList = Array.isArray(fields) ? fields : [fields];
    const url = `/corporate/${corporateId}/settings?tab=financial`;
    const label = 'Financial Inputs';
    super(
      `${fieldList.join(', ')} ${fieldList.length > 1 ? 'are' : 'is'} required but not configured. ` +
      `Update in ${label}: ${url}`
    );
    this.name = 'MissingFinancialInputError';
    this.corporateId = corporateId;
    this.settingsUrl = url;
    this.settingsLabel = label;
    this.fields = fieldList;
  }
}

/**
 * Thrown when required card program settings (billing cycle, payment terms)
 * are not configured. Same pattern as MissingFinancialInputError but routes
 * to the Card Program settings tab.
 */
export class MissingCardProgramInputError extends Error {
  public readonly corporateId: number;
  public readonly settingsUrl: string;
  public readonly settingsLabel: string;
  public readonly fields: string[];

  constructor(fields: string | string[], corporateId: number) {
    const fieldList = Array.isArray(fields) ? fields : [fields];
    const url = `/corporate/${corporateId}/settings?tab=card-program`;
    const label = 'Card Program Settings';
    super(
      `${fieldList.join(', ')} ${fieldList.length > 1 ? 'are' : 'is'} required but not configured. ` +
      `Update in ${label}: ${url}`
    );
    this.name = 'MissingCardProgramInputError';
    this.corporateId = corporateId;
    this.settingsUrl = url;
    this.settingsLabel = label;
    this.fields = fieldList;
  }
}

// ── Interfaces ──

export interface SupplierFinancialProjection {
  supplier_name: string;
  current_annual_volume: number;
  projected_card_volume: number;
  current_payment_costs: number;
  /**
   * `null` when no configured tier or qualifier covers this supplier — NOT `0`.
   * `0` means "a configured rate of 0%"; `null` means "we cannot say". Rendering `null`
   * as `$0` re-introduces R-CE2-REBATE-FALLBACK in a quieter form.
   */
  projected_rebate_earnings: number | null;
  working_capital_benefit: number;
  net_annual_benefit: number;
  roi_percentage: number;
  payback_period_months: number;
  current_tier: string | null;
  projected_tier: string | null;
  volume_to_next_tier: number;
}

export interface CorporateFinancialSummary {
  total_supplier_count: number;
  total_current_volume: number;
  total_projected_card_volume: number;
  total_eliminated_costs: number;
  /**
   * Sum over MATCHED suppliers only. Unmatched suppliers are excluded, never counted as 0.
   * An exclusion nobody can see is just a quieter fabrication, so this number is only
   * honest when read alongside `unmatched_supplier_count` / `unmatched_annual_volume`.
   */
  total_rebate_earnings: number;
  /** How many suppliers were dropped from `total_rebate_earnings` because no tier matched. */
  unmatched_supplier_count: number;
  /** Their combined annual volume — the spend this total could not price. */
  unmatched_annual_volume: number;
  total_working_capital_benefit: number;
  total_implementation_costs: number;
  net_program_benefit: number;
  weighted_average_roi: number;
  program_payback_months: number;
  supplier_projections: SupplierFinancialProjection[];
  setup_required?: boolean;
  missing_setup?: string[];
}

export interface BucketSummary {
  bucket: string;
  supplierCount: number;
  totalSpend: number;
  spendMigrationPct: number;
  projectedSpendEnrollment: number;
  supplierEnrollmentPct: number;
}

export interface TransactionTypeAnalysis {
  transactionType: string;
  suppliers: number;
  totalSpend: number;
  projectedCardSpend: number;
  spendMigrationPct: number;
  supplierEnrollmentPct: number;
  percentOfCardProjection: number;
  percentOfTotalSpend: number;
}

export interface CardEligibilityAnalysis {
  eligibilityStatus: 'matched' | 'not_matched';
  paymentMethod: string;
  suppliers: number;
  totalSpend: number;
  percentage: number;
  transactionCount: number;
  matchSignals: string[];
  reasons?: string[];
}

/**
 * Large Ticket Opportunity Analysis — identifies vendors whose transactions
 * may qualify for large ticket interchange rates.
 *
 * IMPORTANT: Large Ticket is a VENDOR TRANSACTION QUALIFIER, not a program tier.
 * Transactions qualify based on per-transaction dollar amount exceeding a
 * network threshold (Visa > $8K, Mastercard > $10K) AND the vendor passing
 * enhanced Level 2/3 data. This is distinct from volume tiers, which are
 * determined by the corporate's aggregate monthly spend.
 *
 * See: src/docs/specs/CARD_REBATE_STRUCTURE.md
 */
export interface LargeTicketAnalysis {
  totalLargeTicketSpend: number;
  largeTicketSuppliers: number;
  spendMigrationPct: number;
  supplierEnrollmentPct: number;
  projectedSpendEnrollment: number;
  averageTicketSize: number;
  excludedLevelIII: number;
}

export interface WorkingCapitalAnalysis {
  bucket: string;
  suppliers: number;
  totalSpend: number;
  workingCapitalBenefit: number;
  averageBenefitPerSupplier: number;
  paymentTermsAcceleration: number;
}

export interface PaymentMethodBreakdown {
  paymentMethod: string;
  suppliers: number;
  totalSpend: number;
  percentage: number;
  estimatedTransactionCount: number;
}

export interface RevenueShareAnalysis {
  paymentType: string;
  enrollmentRate: number;
  monthlySpend: number;
  revenueSharePct: number;
  monthlyRevenue: number;
  annualSpend: number;
  annualRevenue: number;
}

export interface ExtractedTier {
  id: string;
  tier_name: string;
  tier_order: number;
  rebate_rate: number;
  min_monthly_volume: number;
  max_monthly_volume: number | null;
}

export interface VolumeProjectionResult {
  current_tier: ExtractedTier | null;
  next_tier: ExtractedTier | null;
  volume_to_next_tier: number;
  current_monthly_volume: number;
}

// ── Pure helper calculations ──

/**
 * Eliminated Payment Costs
 *
 * Calculates the cost of the current payment method eliminated by card migration.
 * `supplierTransactionCount` overrides the treasury-level count when per-supplier
 * transaction data is available from the AP spend file.
 */
export function calculateEliminatedPaymentCosts(
  annualVolume: number,
  paymentCost?: CorporatePaymentCost,
  supplierTransactionCount?: number
): number {
  if (!paymentCost) return 0;
  const txnCount = supplierTransactionCount ?? (paymentCost.transaction_count || 0);
  const fixedCosts = txnCount * (paymentCost.cost_per_transaction || 0);
  const percentageCosts = annualVolume * (paymentCost.percentage_cost || 0);
  return fixedCosts + percentageCosts;
}

/**
 * Buyer Working Capital (Float) Benefit
 *
 * card_float = billing_cycle_days + payment_terms_days
 * net_additional_days = max(0, (card_float - acceleration) - current_dpo)
 * discount_rate = WACC + inflation_rate
 * benefit = (volume / 365) × net_additional_days × discount_rate
 *
 * Returns 0 when existing payment terms already exceed net card float.
 * Falls back to degraded processing-days logic when cardProgram is absent.
 */
export function calculateWorkingCapitalBenefit(
  annualVolume: number,
  financialProfile?: CorporateFinancialProfile | null,
  paymentCost?: CorporatePaymentCost,
  cardProgram?: CorporateCardRebateProgram | null,
  supplierDPO?: number,
  accelerationOffered?: number
): number {
  if (!financialProfile) return 0;
  const missingFields: string[] = [];
  if (financialProfile.wacc_rate == null) missingFields.push('WACC Rate');
  if (financialProfile.inflation_rate == null) missingFields.push('Inflation Rate');
  if (missingFields.length > 0) {
    throw new MissingFinancialInputError(missingFields, financialProfile.corporate_id);
  }
  const waccRate = financialProfile.wacc_rate!;
  const inflationRate = financialProfile.inflation_rate!;

  if (cardProgram) {
    const cardProgramMissing: string[] = [];
    if (cardProgram.billing_cycle_days == null) cardProgramMissing.push('Billing Cycle Days');
    if (cardProgram.payment_terms_days == null) cardProgramMissing.push('Payment Terms Days');
    if (cardProgramMissing.length > 0) {
      throw new MissingCardProgramInputError(cardProgramMissing, financialProfile.corporate_id);
    }
    const cardFloat = cardProgram.billing_cycle_days! + cardProgram.payment_terms_days!;
    const acceleration = accelerationOffered ?? 0;
    const currentDPO = supplierDPO ?? (financialProfile.dpo_days ?? FINANCIAL_DEFAULTS.DPO_DAYS);
    const netAdditionalDays = Math.max(0, (cardFloat - acceleration) - currentDPO);
    return (annualVolume / 365) * netAdditionalDays * (waccRate + inflationRate);
  }

  // Degraded fallback (legacy)
  if (!paymentCost) return 0;
  const processingDaysImprovement = Math.max(0, (paymentCost.processing_days || 3) - 1);
  return (annualVolume * processingDaysImprovement / 365) * (waccRate + inflationRate);
}

/**
 * Buyer Float Benefit (full perspective including inflation)
 *
 * net_additional_days = max(0, (card_float - acceleration) - existing_terms)
 * benefit = (spend / 365) × net_additional_days × (WACC + inflation)
 *
 * Returns 0 when existing payment terms already exceed net card float.
 */
export function calculateBuyerFloatBenefit(
  annualSpend: number,
  profile: TradeProfile,
  financialProfile: CorporateFinancialProfile,
  cardProgram: CorporateCardRebateProgram,
  accelerationOfferedOverride?: number
): number {
  const missingFields: string[] = [];
  if (financialProfile.wacc_rate == null) missingFields.push('WACC Rate');
  if (financialProfile.inflation_rate == null) missingFields.push('Inflation Rate');
  if (missingFields.length > 0) {
    throw new MissingFinancialInputError(missingFields, financialProfile.corporate_id);
  }
  const cardProgramMissing: string[] = [];
  if (cardProgram.billing_cycle_days == null) cardProgramMissing.push('Billing Cycle Days');
  if (cardProgram.payment_terms_days == null) cardProgramMissing.push('Payment Terms Days');
  if (cardProgramMissing.length > 0) {
    throw new MissingCardProgramInputError(cardProgramMissing, financialProfile.corporate_id);
  }
  const waccRate = financialProfile.wacc_rate!;
  const inflationRate = financialProfile.inflation_rate!;
  const existingTerms = profile.avgActualPaymentTerms || FINANCIAL_DEFAULTS.DPO_DAYS;
  const accelerationOffered = accelerationOfferedOverride ?? 0;
  const cardFloat = cardProgram.billing_cycle_days! + cardProgram.payment_terms_days!;
  const netAdditionalDays = Math.max(0, (cardFloat - accelerationOffered) - existingTerms);
  return (annualSpend / 365) * netAdditionalDays * (waccRate + inflationRate);
}

/** @deprecated Use `calculateBuyerFloatBenefit` (buyer) or `calculateSupplierAccelerationBenefit` (supplier). */
export const calculateSupplierWorkingCapitalImpact = calculateBuyerFloatBenefit;

/**
 * Supplier Working Capital Benefit (Acceleration)
 *
 * The supplier benefits from receiving payment faster when accepting card.
 * accelerationDays = max(0, currentPaymentTerms - proposedCardTerms)
 * benefit = (annualSpend / 365) * accelerationDays * supplierCostOfCapital
 *
 * Uses the supplier's own cost of capital (not the buyer's WACC).
 * `supplierCostOfCapital` is required — callers must provide it explicitly.
 */
export function calculateSupplierAccelerationBenefit(
  annualSpend: number,
  currentPaymentTermsDays: number,
  proposedCardTermsDays: number,
  supplierCostOfCapital: number
): number {
  const costOfCapital = supplierCostOfCapital;
  const accelerationDays = Math.max(0, currentPaymentTermsDays - proposedCardTermsDays);
  return (annualSpend / 365) * accelerationDays * costOfCapital;
}

/** Calculate npv. */
export function calculateNPV(annualCashFlow: number, discountRate: number, periods: number): number {
  let npv = 0;
  const monthlyCashFlow = annualCashFlow / 12;
  const monthlyDiscountRate = discountRate / 12;
  for (let period = 1; period <= periods; period++) {
    npv += monthlyCashFlow / Math.pow(1 + monthlyDiscountRate, period);
  }
  return npv;
}

/** Extract tiers from program from source data. */
export function extractTiersFromProgram(program: CorporateCardRebateProgram): ExtractedTier[] {
  const tiers: ExtractedTier[] = [];
  for (let i = 1; i <= 10; i++) {
    const tierName = program[`tier_${i}_name` as keyof CorporateCardRebateProgram] as string | null;
    const rebateRate = program[`tier_${i}_rebate_rate` as keyof CorporateCardRebateProgram] as number | null;
    const minVolume = program[`tier_${i}_min_volume` as keyof CorporateCardRebateProgram] as number | null;
    const maxVolume = program[`tier_${i}_max_volume` as keyof CorporateCardRebateProgram] as number | null;
    if (tierName && rebateRate !== null) {
      tiers.push({
        id: `tier_${i}`,
        tier_name: tierName,
        tier_order: i,
        rebate_rate: rebateRate,
        min_monthly_volume: minVolume || 0,
        max_monthly_volume: maxVolume
      });
    }
  }
  return tiers;
}

/** Calculate volume projection. */
export function calculateVolumeProjection(program: CorporateCardRebateProgram, monthlyVolume: number): VolumeProjectionResult {
  const tiers = extractTiersFromProgram(program);
  let currentTier: ExtractedTier | null = null;
  let nextTier: ExtractedTier | null = null;

  for (const tier of tiers) {
    if (monthlyVolume >= tier.min_monthly_volume &&
        (tier.max_monthly_volume === null || monthlyVolume <= tier.max_monthly_volume)) {
      currentTier = tier;
      break;
    }
  }

  if (currentTier) {
    const currentIndex = tiers.findIndex(t => t.id === currentTier!.id);
    if (currentIndex < tiers.length - 1) {
      nextTier = tiers[currentIndex + 1];
    }
  } else if (tiers.length > 0) {
    nextTier = tiers[0];
  }

  return {
    current_tier: currentTier,
    next_tier: nextTier,
    volume_to_next_tier: nextTier ? Math.max(0, nextTier.min_monthly_volume - monthlyVolume) : 0,
    current_monthly_volume: monthlyVolume
  };
}

/** Categorize suppliers by spend. */
export function categorizeSuppliersBySpend(spend: number): string {
  if (spend < SPEND_BUCKETS.SMALL.max) return SPEND_BUCKETS.SMALL.label;
  if (spend < SPEND_BUCKETS.MEDIUM.max) return SPEND_BUCKETS.MEDIUM.label;
  if (spend < SPEND_BUCKETS.LARGE.max) return SPEND_BUCKETS.LARGE.label;
  if (spend < SPEND_BUCKETS.STRATEGIC_MID.max) return SPEND_BUCKETS.STRATEGIC_MID.label;
  return SPEND_BUCKETS.STRATEGIC_HIGH.label;
}
